import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { Formik } from 'formik';
import toast from 'react-hot-toast';
import { useNavigate, useLocation } from 'react-router-dom';
import * as Yup from 'yup';
import { useMutation } from '@tanstack/react-query';
import { IconEye, IconEyeOff } from '@tabler/icons-react';
import { useEffect } from 'react';

import { API_URL, post } from 'lib/network/api';
import { storeAccessToken } from 'lib/network/jwt-token-access';
import { useAuthModal } from 'context/AuthModalContext';
import { fetchProfile } from 'store/actions/profileActions';

import InputText from 'components/InputText';
import Modal from './Modal';

const ModalAuth = ({ open, setOpen }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [showPassword, setShowPassword] = useState(false);
  const { type } = useAuthModal();

  // Auto close modal when on /register page
  useEffect(() => {
    if (location.pathname === '/register' && open) {
      setOpen(false);
    }
  }, [location.pathname, open, setOpen]);

  const title = type === 'expired' ? 'Your session has expired' : 'It seems you’re not logged in';

  const subtitle =
    type === 'expired'
      ? 'Please log in again to continue your session.'
      : 'To book classes online, please create an account with us!';

  const LoginSchema = Yup.object().shape({
    email: Yup.string().email('Invalid email format').required('Email is required'),
    password: Yup.string()
      .required('Password is required')
      .min(6, 'Password must be at least 6 characters'),
  });

  const loginMutation = useMutation({
    mutationFn: async (values) => {
      const data = {
        email: values.email,
        password: values.password,
      };
      return await post(`${API_URL}/api/login`, data);
    },
    onSuccess: (res) => {
      storeAccessToken(res.access_token, res.refresh_token);
      dispatch(fetchProfile());
      toast.success('Login successful!');
      setOpen(false);
    },
    onError: (error) => {
      toast.error(error?.detail || 'Login failed. Please try again.');
    },
  });

  return (
    <Modal open={open} onRequestClose={() => setOpen(false)} clickOverlay={false}>
      <div className="bg-white p-4 sm:p-8 rounded-xl w-full max-w-[500px] sm:w-[500px]">
        <div>
          <div className="text-center mb-6">
            <h2 className="text-xl mx-auto font-bold leading-9 tracking-tight text-kin-text w-[250px]">
              {title}
            </h2>
            <span className="font-light text-sm text-kin-text mt-3 block">{subtitle}</span>
          </div>
          {/* <div className="my-6 grid grid-cols-1 gap-4">
              <a
                href="/login"
                className="flex w-full items-center justify-center gap-3 rounded-[20px] bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus-visible:ring-transparent">
                <svg viewBox="0 0 24 24" aria-hidden="true" className="h-5 w-5">
                  <path
                    d="M12.0003 4.75C13.7703 4.75 15.3553 5.36002 16.6053 6.54998L20.0303 3.125C17.9502 1.19 15.2353 0 12.0003 0C7.31028 0 3.25527 2.69 1.28027 6.60998L5.27028 9.70498C6.21525 6.86002 8.87028 4.75 12.0003 4.75Z"
                    fill="#EA4335"
                  />
                  <path
                    d="M23.49 12.275C23.49 11.49 23.415 10.73 23.3 10H12V14.51H18.47C18.18 15.99 17.34 17.25 16.08 18.1L19.945 21.1C22.2 19.01 23.49 15.92 23.49 12.275Z"
                    fill="#4285F4"
                  />
                  <path
                    d="M5.26498 14.2949C5.02498 13.5699 4.88501 12.7999 4.88501 11.9999C4.88501 11.1999 5.01998 10.4299 5.26498 9.7049L1.275 6.60986C0.46 8.22986 0 10.0599 0 11.9999C0 13.9399 0.46 15.7699 1.28 17.3899L5.26498 14.2949Z"
                    fill="#FBBC05"
                  />
                  <path
                    d="M12.0004 24.0001C15.2404 24.0001 17.9654 22.935 19.9454 21.095L16.0804 18.095C15.0054 18.82 13.6204 19.245 12.0004 19.245C8.8704 19.245 6.21537 17.135 5.2654 14.29L1.27539 17.385C3.25539 21.31 7.3104 24.0001 12.0004 24.0001Z"
                    fill="#34A853"
                  />
                </svg>
                <span className="text-sm font-semibold leading-6">Log in with Google</span>
              </a>
            </div> */}

          <div className="relative font-bold text-kin-text">
            <div aria-hidden="true" className="absolute inset-0 flex items-center">
              <div className="w-full border-t-[0.5px] border-kin-text" />
            </div>
            <div className="relative flex justify-center">
              <span className="bg-white px-3 text-sm">Or</span>
            </div>
          </div>

          <div className="mt-4">
            <div>
              <Formik
                initialValues={{ email: '', password: '' }}
                validationSchema={LoginSchema}
                onSubmit={(values) => loginMutation.mutate(values)}>
                {({ handleSubmit }) => (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Email */}
                    <InputText name="email" type="email" label="EMAIL ADDRESS" required />

                    {/* Password */}
                    <InputText
                      name="password"
                      label="PASSWORD"
                      type={showPassword ? 'text' : 'password'}
                      icon={
                        showPassword ? (
                          <IconEyeOff className="text-primary-main h-5 w-5" />
                        ) : (
                          <IconEye className="text-gray-400 h-5 w-5" />
                        )
                      }
                      clickicon={() => setShowPassword(!showPassword)}
                      required
                    />

                    {/* <div className="flex items-center justify-between">
                          <div className="flex items-center">
                            <input
                              id="remember-me"
                              name="remember-me"
                              type="checkbox"
                              className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                            />
                            <label
                              htmlFor="remember-me"
                              className="ml-3 block text-sm leading-6 text-gray-700">
                              Remember me
                            </label>
                          </div>

                          <div className="text-sm leading-6">
                            <a
                              href="#"
                              className="font-semibold text-indigo-600 hover:text-indigo-500">
                              Forgot password?
                            </a>
                          </div>
                        </div> */}

                    {/* Submit Button */}
                    <button
                      type="submit"
                      disabled={loginMutation.isPending}
                      className="w-full bg-primary-main text-white rounded-[100px] py-3 px-3 flex justify-center items-center disabled:opacity-60">
                      {loginMutation.isPending ? (
                        <span className="text-xs font-semibold">PROCESSING...</span>
                      ) : (
                        <span className="text-xs font-semibold">LOG IN</span>
                      )}
                    </button>

                    {/* Footer */}
                    <p className="mt-6 text-center text-sm text-gray-500 font-light">
                      Don&apos;t have an account yet?{' '}
                      <button
                        type="button"
                        onClick={() => navigate('/register')}
                        className="text-kin-text font-semibold hover:text-primary-dark">
                        Sign Up
                      </button>
                    </p>
                  </form>
                )}
              </Formik>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default ModalAuth;
